import { inject, Injectable } from "@angular/core";
import { environment } from "@environments/environment";
import { beginIntercomBoot, identifyIntercom, IntercomFlow, isIntercomSessionCurrent } from "@handshake/core-fe";
import { catchError, of, tap } from "rxjs";
import { UserService } from "../apis/user.service";
import { User } from "../entities/user.entity";

@Injectable({
  providedIn: "root",
})
export class IntercomSessionService {
  private readonly userService = inject(UserService);

  boot(user: User, options: { businessId?: string; flow: IntercomFlow }) {
    if (!environment.intercom?.appId) {
      return of(undefined);
    }

    const bootEpoch = beginIntercomBoot();

    return this.userService.getIntercomJwt(options.businessId).pipe(
      tap((response) => {
        if (!isIntercomSessionCurrent(bootEpoch)) {
          return;
        }
        const token = this.resolveToken(response);
        if (!token) {
          console.warn("[Intercom] JWT response missing token", response);
          return;
        }
        identifyIntercom(environment.intercom, {
          jwt: token,
          user: {
            id: user.id,
            email: user.email,
            name: `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim(),
            createdAtUnix: user.createdAt ? Math.floor(new Date(user.createdAt).getTime() / 1000) : undefined,
          },
          attrs: {
            app: "business-fe",
            role: "business",
            flow: options.flow,
            businessId: options.businessId,
          },
        });
      }),
      catchError((error) => {
        console.warn("[Intercom] failed to boot session", error);
        return of(undefined);
      })
    );
  }

  private resolveToken(response: unknown): string | undefined {
    if (!response) {
      return undefined;
    }
    if (typeof response === "string") {
      return response;
    }
    if (typeof response === "object") {
      const body = response as { token?: unknown; data?: { token?: unknown } };
      if (typeof body.token === "string") {
        return body.token;
      }
      if (typeof body.data?.token === "string") {
        return body.data.token;
      }
    }
    return undefined;
  }
}
